import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import Card from '@components/shared/Card/Card';
import { Button } from '@components/shared/Button/Button';
import { useTexts } from 'utils/useTexts';
import { useFormContext } from '@context/FormContext';
import getOfferTypeIcon from 'utils/getOfferTypeIcon';
import { IAccurateEstimatedOfferResponse } from '@services/getAccurateEstimatedOffer';
import { Error } from '@components/shared/form/Error';
import { postSelectedOffer } from '@services/postSelectedOffer';
import { CDX_ACTIONS } from 'types/form';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { handleFetchOffers } from '@services/softPullHandler';
import { getContactData } from '@services/utils';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { setLocalStorage } from 'utils/setLocalStorage';
import { camelToSnake } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

const createOfferObject = (offer: IAccurateEstimatedOfferResponse, index: number, totalOffers: number) => {
  return {
    deal_id: offer?.dealId ?? '',
    offer_id: `offer-${index + 1}`,
    offer_rate: offer?.rate ?? '',
    offer_term: offer?.term ?? '',
    offer_payment: offer?.monthlyPaymentAmount ?? '',
    offer_position: index + 1,
    is_best_offer: index === 0,
    total_offers_presented: totalOffers,
    offer_type: camelToSnake(offer?.offerType ?? ''),
  };
};

const OfferSelectionForm = () => {
  const { t } = useTexts();
  const contactData = getContactData();
  const { next } = useScreenNavigator();
  const { trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.OFFERS);
  const { currentStep, softPullResponse, dispatch, submitStart, offerSelectionFormData, submitSuccess, isLoading } =
    useFormContext();
  const hasCalledfetchSoftPullDataRef = useRef(false);

  const [selectedID, setSelectedID] = useState<string | undefined>();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [offerSelectedValidation, setOfferSelectedValidation] = useState('');
  const [softPullResponseData, setSoftPullResponseData] = useState<IAccurateEstimatedOfferResponse[] | null | undefined>(null);
  const [isSoftPullDataLoading, setIsSoftPullDataLoading] = useState(false);

  useEffect(() => {
    if (hasCalledfetchSoftPullDataRef.current) return;
    hasCalledfetchSoftPullDataRef.current = true;
    const fetchSoftPullData = async () => {

      const softpullRequestPayload = {
        contactId: contactData?.contactId ?? '',
        leadId: contactData?.leadId ?? '',
      };
      if (!softPullResponse) {
        setIsSoftPullDataLoading(true);
        const data = await handleFetchOffers({ softpullRequestPayload, currentStep, next, dispatch, trackFormSubmit });
        setSoftPullResponseData(data);
        if (data && data.length > 0) {
          data.forEach((offer, index) => {
            const offersSegment = createOfferObject(offer, index, data.length);
            trackFormSubmit('Offers Presented', offersSegment);
          });
        } else {
          trackFormSubmit('Offers Presented', {
            offer_type: 'no-offer',
            total_offers_presented: 0,
          });
        }
        setIsSoftPullDataLoading(false);
      } else {
        setSoftPullResponseData(softPullResponse);
        softPullResponse.forEach((offer, index) => {
          const offersSegment = createOfferObject(offer, index, softPullResponse.length);
          trackFormSubmit('Offers Presented', offersSegment);
        });
      }
    };

    fetchSoftPullData();
  }, [softPullResponse]);

  const handleOfferSelection = (id: string | undefined, offer: IAccurateEstimatedOfferResponse, index: number) => {
    setSelectedID(id);
    setSelectedIndex(index);
    setOfferSelectedValidation('');
    setLocalStorage(offer, 'selectedOffer');
    dispatch({ type: 'SET_OFFER_SELECTION', payload: offer });
    startInteraction();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (softPullResponseData && selectedID) {
      setOfferSelectedValidation('');

      submitStart();
      const submitResponse = await postSelectedOffer(selectedID ?? '');
      if ('error' in submitResponse) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: submitResponse,
        });
      } else {
        if (offerSelectionFormData && selectedIndex !== null) {
          trackFormSubmit('Offer Selected', {
            ...createOfferObject(offerSelectionFormData, selectedIndex, softPullResponse?.length ?? 0),
            savings_amount: Math.abs(Number(contactData?.currentMonthlyPayment) - offerSelectionFormData.monthlyPaymentAmount),
            value: 200,
            currency: 'USD',
          });
          
          trackFormSubmit('Form Submitted', {
            submission_status: true,
            forward_screen_name: 'address_loop',
          });
        }
        submitSuccess();
        //TODO: check if this is going to address page or not
        next(currentStep, 'postSubmitOutcome', { submitResponse }, true);
      }
    } else {
      setOfferSelectedValidation(t('offerSelection.validation'));
    }
  };

  useEffectOnce(() => trackPage(t('pageTitles.OfferSelection')));

  return (
    <>
      <form onSubmit={handleSubmit}>
        <h1 className="iLendingCDX-form-title">{t('offerSelection.title')}</h1>
        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4">{t('offerSelection.chooseTheBest')}</h2>
        <div className="iLendingCDX-mb-4">
          {softPullResponseData &&
            softPullResponseData.map((offer, index) => {
              const id = offer?.dealId ?? useId();
              return (
                <Card
                  key={id}
                  id={offer?.dealId ?? ''}
                  icon={getOfferTypeIcon(offer?.offerType)}
                  term={offer?.term}
                  name={offer?.offerType}
                  price={offer?.monthlyPaymentAmount}
                  interest={offer?.rate}
                  selected={selectedID}
                  offer={offer}
                  handleOfferSelection={() => handleOfferSelection(offer?.dealId ?? '', offer, index)}
                />
              );
            })}
          <div className="iLendingCDX-text-center">
            {offerSelectedValidation.length > 0 && <Error errorMessage={offerSelectedValidation} />}
          </div>
        </div>
        <Button
          className="iLendingCDX-button--rounded"
          size="lg"
          type="submit"
          isLoading={isLoading}
        >
          {t('offerSelection.continue')}
        </Button>
      </form>
      {isSoftPullDataLoading && <OverlaySpinner size="lg" />}
    </>
  );
};

export default OfferSelectionForm;
